import { useRouter } from 'expo-router';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import theme from 'shared/styles/theme';

const VerifySsnErrorPage = () => {
  const router = useRouter();
  const { handleLogout } = useContext<AuthContextProps>(AuthContext);
  const { t } = useTranslation('reactivation', { keyPrefix: 'verifySsnError' });
  const insets = useSafeAreaInsets();

  useViewDidFocusScreenAnalyticsEvent({
    key: 'verify-ssn-error-reactivation',
    customAttributes: {
      reactivation_flow: 'true',
    },
  });

  const handleSignup = async () => {
    await handleLogout();
    router.push('/signup');
  };

  const handleBackToHome = () => {
    router.replace?.('/reactivation');
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.buttonContainer}>
        <Button
          label={t('logoutButton')}
          analyticsId="logoutButtonPressed"
          customAttributes={{
            reactivation_flow: 'true',
          }}
          onPress={async () => {
            await handleLogout();
          }}
          type="TEXT"
          size="LARGE"
          width="HUG"
        />
      </View>
      <View style={styles.textContainer}>
        <View>
          <TitleXLText>{t('title')}</TitleXLText>
        </View>
        <BodyBaseText style={styles.description}>
          {t('description')}
        </BodyBaseText>
      </View>
      <View
        style={[
          styles.bottomButtonContainer,
          { paddingBottom: insets.bottom + theme.spacing.m },
        ]}
      >
        <Button
          label={t('signupButton')}
          onPress={handleSignup}
          analyticsId="signupButtonPressed"
          customAttributes={{
            reactivation_flow: 'true',
          }}
          size="LARGE"
          type="PRIMARY"
          width="FULL"
        />
        <Button
          label={t('backToHomeButton')}
          analyticsId="goToHomeButtonPressed"
          customAttributes={{
            reactivation_flow: 'true',
          }}
          onPress={handleBackToHome}
          size="LARGE"
          type="SECONDARY"
          width="FULL"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  bottomButtonContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    gap: theme.spacing.m,
    justifyContent: 'flex-end',
  },
  buttonContainer: {
    alignItems: 'flex-end',
    display: 'flex',
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    zIndex: 1,
  },
  description: {
    color: theme.color.text.primary,
  },
  textContainer: {
    alignSelf: 'stretch',
    display: 'flex',
    gap: theme.spacing.m,
    paddingTop: theme.spacing.m,
    width: '100%',
  },
});

export default VerifySsnErrorPage;
